/**
 * Lejárt munkamenet — KÖZÖS JELZŐ a három kérés-út számára.
 * [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03]
 *
 * MIÉRT: a webes UX-jelentés 4.5 pontja szerint a 401 a `/` útvonalon nem irányított át: az
 * egyetlen 401-kezelő (`services/authService.ts`, az `api` példány interceptora) csendben törölte
 * a tokent, és CSAK akkor ugrott a `/`-ra, ha a cím nem `/` volt — a Dashboard viszont a `/`-on
 * fut, így a felület „bejelentkezve" maradt, miközben minden további kérés hibát adott. Ráadásul
 * a 401 három különböző úton érkezik (a leltár 2(a) szakasza szerint mérve):
 *   1) az `api` (`axios.create`) példány — a saját interceptorával,
 *   2) a GLOBÁLIS `axios` (eventService, attendanceService, coachPaymentService, … AdminMessages)
 *      — az `axios.create()` példány interceptorát NEM örökli, tehát ezt külön kell bekötni,
 *   3) a nyers `fetch` (EventAttendance — benne a jelenléti ív MENTÉSE —, NewsManagement,
 *      UserProfile, UserProfileDialog) — ezt semmilyen interceptor nem látja.
 * Mindhárom út ezt az EGY jelzőt hívja; az `AuthContext` iratkozik fel rá, és ő dönti el, hogy
 * azonnal kijelentkeztet, vagy (mentetlen adatnál) újra-belépő ablakot nyit.
 *
 * [Web 7 / T1, 2026-10-05] A jelzés ELŐTT mindhárom úton egy csendes bejelentkezés-megújítás jön
 * (`utils/tokenRefresh.ts`): közel lejárt / lejárt tokennél a kérés indulásakor, 401-re egyszer a
 * kérés megismétlésével. Ide CSAK az jut, amit a megújítás sem tudott megoldani (30 napnál régebbi /
 * hamis token, inaktív fiók). A nyers `fetch`-út ma a közös `authFetch` burkolón megy (`utils/authFetch.ts`).
 *
 * EGYSZERISÉG: a Dashboard betöltéskor sok kérést indít párhuzamosan → N db 401. A zár miatt a
 * jelzés EGYSZER fut le, és a zár addig áll, amíg egy sikeres belépés (`resetSessionExpired`)
 * fel nem oldja — így nincs N kijelentkeztetés, N üzenet, és nincs végtelen kör.
 */

export type SessionExpiredListener = () => void;

let expired = false;
const listeners = new Set<SessionExpiredListener>();

/** Lejárt munkamenet jelzése. A zár miatt csak az ELSŐ hívás értesít, a többi hatástalan. */
export function notifySessionExpired(): void {
  if (expired) return;
  expired = true;
  // Másolat: egy feliratkozó a futás közben leiratkozhat.
  Array.from(listeners).forEach((cb) => {
    try {
      cb();
    } catch (err) {
      console.error('Session-expired listener failed:', err);
    }
  });
}

/** Feliratkozás a jelzésre; a visszaadott függvény leiratkoztat. */
export function onSessionExpired(cb: SessionExpiredListener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/**
 * [7. javító kör — validátor M1, 2026-10-04] „Helyreállt a munkamenet" jelzés: a lejárt-zár egy
 * sikeres (újra)belépéssel oldódott fel. A képernyők ebből tudják, hogy egy LEJÁRAT MIATT bukott
 * betöltést újra lehet indítani (pl. `CoachPaymentManagement` kifizethető-lista). Csak akkor szól,
 * ha a zár valóban állt — egy sima belépésnél (nincs zár) nincs teendő.
 */
const restoredListeners = new Set<SessionExpiredListener>();

export function onSessionRestored(cb: SessionExpiredListener): () => void {
  restoredListeners.add(cb);
  return () => {
    restoredListeners.delete(cb);
  };
}

/** A zár feloldása — sikeres (újra)belépés után hívandó. */
export function resetSessionExpired(): void {
  const wasExpired = expired;
  expired = false;
  reauthDialogOpen = false;
  if (!wasExpired) return;
  Array.from(restoredListeners).forEach((cb) => {
    try {
      cb();
    } catch (err) {
      console.error('Session-restored listener failed:', err);
    }
  });
}

/** Áll-e most a lejárt-zár (tesztekhez és a feliratkozóknak). */
export function isSessionExpiredLocked(): boolean {
  return expired;
}

/**
 * [7. javító kör — validátor F2, 2026-10-04] Nyitva van-e MOST az újra-belépő ablak.
 *
 * MI VOLT A BAJ: a „… a változásaid megmaradtak …" mondatot (`SESSION_EXPIRED_SAVE_MESSAGE`) a
 * mentési hibaszöveg CSAK a lejárt-ZÁR alapján adta. Mentetlen adat NÉLKÜLI 401-nél viszont nincs
 * ablak: azonnali kijelentkeztetés jön (`AuthContext` lejárat-kezelője), a `showError` toast pedig
 * (a `FeedbackProvider` az `AuthProvider` FÖLÖTT él, `App.tsx`) túléli a Dashboard lebontását —
 * a `/belepes`-en a „Lejárt a bejelentkezésed" MELLETT „a változásaid megmaradtak" is látszott
 * (mérve: `MyPayments` üres megjegyzéssel „Igazolom", `VenueManagement` változatlan mentés).
 *
 * MOST: az `AuthContext` SZINKRON állítja ezt a jelzőt, amikor az ablakot megnyitja/bezárja (a
 * lejárat-kezelő a 401-et átengedő interceptorban, a komponens `catch`-e ELŐTT fut), és a
 * hibaszöveg EBBŐL dönti el, hogy a „megmaradtak" mondat igaz-e. A zár feloldása is törli.
 */
let reauthDialogOpen = false;

export function setReauthDialogOpen(open: boolean): void {
  reauthDialogOpen = open;
}

export function isReauthDialogOpen(): boolean {
  return reauthDialogOpen;
}

/**
 * [7. javító kör — validátor F2] Egy HIBA-toast elnyomandó-e, mert a munkamenet ablak nélkül ért
 * véget, és a belépő oldal UGYANEZT mondja („Lejárt a bejelentkezésed, lépj be újra." —
 * `sessionNoticeText('expired')`). A belépő oldalon EGY üzenet legyen, ne kettő egymás alatt.
 * Csak a pontosan egyező lejárat-mondatot nyeli el, minden más hiba-toast megy tovább.
 */
export function shouldSuppressSessionToast(text: string): boolean {
  if (!expired || reauthDialogOpen) return false;
  return text === SESSION_EXPIRED_MESSAGE || text === SESSION_EXPIRED_SAVE_MESSAGE;
}

/**
 * Azok a hitelesítési végpontok, amelyek 401-e NEM lejárt munkamenetet jelent:
 *   - `/auth/login`: rossz jelszó / inaktív / jóváhagyásra váró fiók (`AuthController.cs:50,55,60`)
 *     — ezt a belépő űrlap maga írja ki;
 *   - `/auth/logout`: a lejárt tokennel küldött kilépés 401-e — különben önmagát hívó kör lenne;
 *   - `/auth/refresh`: a csendes megújítás saját elutasítása — a lejáratot ilyenkor a megújításra váró
 *     eredeti kérés 401-e jelzi (`utils/tokenRefresh.ts`, [Web 7 / T1, 2026-10-05]);
 *   - a nyilvános auth-végpontok (regisztráció, e-mail megerősítés, jelszó-visszaállítás).
 */
const AUTH_FLOW_URL = /\/auth\/(login|logout|refresh|register|verify-email|forgot-password|reset-password)/i;

export function isAuthFlowUrl(url: string | undefined | null): boolean {
  return !!url && AUTH_FLOW_URL.test(url);
}

/**
 * A küldött `Authorization` fejlécből a token kinyerése. A régi hívó-helyek a `Bearer ${token}`
 * mintát token nélkül is elküldik („Bearer null"), ezt token NÉLKÜLI kérésnek vesszük.
 */
export function tokenFromAuthorizationHeader(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  const match = /^Bearer\s+(.+)$/i.exec(header.trim());
  if (!match) return null;
  const token = match[1].trim();
  if (!token || token === 'null' || token === 'undefined') return null;
  return token;
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

/**
 * Eldönti, hogy egy válasz lejárt munkamenetet jelent-e, és ha igen, jelez.
 *
 * Feltételek (mind kell):
 *   - a státusz 401 (a 403 jogosultság-hiány, NEM kijelentkeztetés; hálózati hibánál nincs státusz);
 *   - nem az auth-folyamat saját végpontja (lásd fent);
 *   - a kérés TOKENNEL ment ki — a KÜLDÖTT fejléc dönt, nem a pillanatnyi tárolt állapot;
 *   - a küldött token azonos a MOST tárolt tokennel. Ha közben új token került a tárolóba
 *     (újra-belépés, másik fülön belépés), a régi kérés 401-e elavult, és nem dobhatja ki az új
 *     munkamenetet; ha a tároló már üres (kézi kijelentkezés vagy már kezelt lejárat), szintén
 *     nincs teendő — így a kézi kijelentkezés utáni késő 401 sem ír ki „lejárt" üzenetet.
 *
 * @returns true, ha a válasz lejárt munkamenetnek minősült (a jelzés megtörtént vagy a zár már állt).
 */
export function reportUnauthorized(
  status: number | undefined | null,
  url: string | undefined | null,
  sentToken: string | null | undefined
): boolean {
  if (status !== 401) return false;
  if (isAuthFlowUrl(url)) return false;
  if (!sentToken) return false;
  if (readStoredToken() !== sentToken) return false;
  notifySessionExpired();
  return true;
}

/**
 * Axios-hiba (példány VAGY globális) vizsgálata. A küldött tokent a kérés konfigurációjának
 * fejlécéből olvassuk (axios 1.x: `AxiosHeaders`, `get()`-tel; régebbi alak: sima objektum).
 */
export function reportAxiosUnauthorized(error: any): boolean {
  const status = error?.response?.status;
  if (status !== 401) return false;
  const config = error?.config ?? {};
  const headers = config.headers;
  let header: unknown;
  if (headers && typeof headers.get === 'function') {
    header = headers.get('Authorization');
  }
  if (header === undefined || header === null) {
    header = headers?.Authorization ?? headers?.authorization;
  }
  return reportUnauthorized(status, config.url, tokenFromAuthorizationHeader(header));
}

/**
 * A hitelesített `fetch`-ágak közös 401-ellenőrzése: a válasz megérkezése UTÁN, a `response.ok`
 * vizsgálata ELŐTT hívandó, a kéréshez HASZNÁLT tokennel. [Web 7 / T1, 2026-10-05] Ma EGYETLEN hívója
 * a közös `authFetch` burkoló (`utils/authFetch.ts`), a csendes megújítás és a 401 utáni egyszeri
 * ismétlés UTÁN — a hívó-helyek nem hívják külön. Nyilvános `fetch` (NewsReader, PublicLanding) NEM hívja.
 *
 * @returns true, ha a 401 lejárt munkamenetet jelentett — a hívó ilyenkor érthető szöveget mutathat
 *          a nyers „(HTTP 401)" helyett (lásd `SESSION_EXPIRED_SAVE_MESSAGE`).
 */
export function reportFetchUnauthorized(
  response: { status: number; url?: string } | null | undefined,
  sentToken: string | null | undefined,
  url?: string
): boolean {
  if (!response) return false;
  return reportUnauthorized(response.status, url ?? response.url, sentToken === 'null' ? null : sentToken);
}

/** A lejárt munkamenet egységes mondata (a jelentés 4.5 / 4.4 javaslata — ideiglenes alapértelmezés). */
export const SESSION_EXPIRED_MESSAGE = 'Lejárt a bejelentkezésed, lépj be újra.';

/** Mentés közben jött lejárat: a mentetlen adat a képernyőn marad (újra-belépő ablak). */
export const SESSION_EXPIRED_SAVE_MESSAGE =
  'Lejárt a bejelentkezésed — lépj be újra, a változásaid megmaradtak, utána mentsd el őket ismét.';

/**
 * [2. javító kör — validátor M-1, 2026-10-03] Axios-os MENTÉS hibájának szövege lejárt munkamenetnél.
 *
 * MIÉRT: a mentetlen-adatú űrlapokon a 401 után az újra-belépő ablak nyílik („a változásaid
 * megmaradtak"), de az űrlap saját hibasávja a régi, általános mondatot írta (pl. az
 * `EventEditDialog` „Hiba történt az esemény frissítésekor", a `PlannedEventDialog` az axios ANGOL
 * „Request failed with status code 401"-ét) — ellentmondott az ablaknak.
 *
 * Akkor ad mondatot, ha a válasz 401 ÉS a lejárt-zár áll: a zárat a 401-et átengedő interceptor
 * (`services/authService.ts`) MÉG a komponens `catch`-e ELŐTT állítja, és csak sikeres (újra)belépés
 * oldja. Így egy elavult (közben lecserélt) token 401-e vagy a belépés saját 401-e (rossz jelszó)
 * NEM kapja ezt a mondatot.
 *
 * MIÉRT NEM az `apiErrors.ts` `apiErrorMessage`-ében: (1) annak magja a mobil tükörrel közös, és a
 * paritás-őr (`utils/__tests__/apiErrors.parity.test.ts`) ugyanazt a viselkedést kéri mindkét
 * oldalon — a `mobile/` ebben a körben érintetlen; (2) az `apiErrorMessage` a belépő űrlapon is fut,
 * ahol a 401 a szerver magyar indoklása („A fiók inaktív", „Helytelen e-mail cím vagy jelszó"),
 * és azt nem írhatja felül a lejárat-mondat.
 *
 * @returns a `SESSION_EXPIRED_SAVE_MESSAGE`, vagy `null` (a hívó a saját szövegét adja).
 */
export function sessionExpiredSaveMessage(err: any): string | null {
  if (err?.response?.status !== 401) return null;
  if (isAuthFlowUrl(err?.config?.url)) return null;
  if (!expired) return null;
  // [7. javító kör — validátor F2] a „megmaradtak" mondat CSAK nyitott újra-belépő ablaknál igaz;
  // ablak nélkül (azonnali kijelentkeztetés) a rövid, igaz lejárat-mondat.
  return reauthDialogOpen ? SESSION_EXPIRED_SAVE_MESSAGE : SESSION_EXPIRED_MESSAGE;
}

/**
 * A belépő oldal üzenetének `sessionStorage`-jelzője: túléli a teljes újratöltést, a következő
 * SIKERES belépés törli, a kézi kijelentkezés nem állítja.
 */
export const SESSION_NOTICE_KEY = 'auth_session_notice';

/**
 * [2. javító kör, 2026-10-03] Két új ok, hogy a belépő oldal ne állítson valótlant:
 *   - `otherUserReauth` (validátor M-2): az újra-belépő ablakban a szerver MÁS felhasználót adott
 *     vissza — korábban „Lejárt a bejelentkezésed" jelent meg;
 *   - `permissionChanged`: az újra-belépés (vagy egy másik fülön történt belépés) után ugyanannak a
 *     felhasználónak megváltozott a szerepköre / állapota (`AuthContext` `hasPermissionChange`).
 */
export type SessionNotice = 'expired' | 'otherTabLogout' | 'otherUser' | 'otherUserReauth' | 'permissionChanged';

const SESSION_NOTICES: readonly SessionNotice[] = [
  'expired',
  'otherTabLogout',
  'otherUser',
  'otherUserReauth',
  'permissionChanged',
];

export function setSessionNotice(notice: SessionNotice): void {
  try {
    sessionStorage.setItem(SESSION_NOTICE_KEY, notice);
  } catch {
    /* privát mód / letiltott tároló: az üzenet csak a mostani nézetben látszik */
  }
}

export function getSessionNotice(): SessionNotice | null {
  try {
    const v = sessionStorage.getItem(SESSION_NOTICE_KEY);
    return (SESSION_NOTICES as readonly string[]).includes(v ?? '') ? (v as SessionNotice) : null;
  } catch {
    return null;
  }
}

export function clearSessionNotice(): void {
  try {
    sessionStorage.removeItem(SESSION_NOTICE_KEY);
  } catch {
    /* lásd fent */
  }
}

/** A belépő oldalon megjelenő mondat a jelző értéke szerint. */
export function sessionNoticeText(notice: SessionNotice | null): string | null {
  switch (notice) {
    case 'expired':
      return SESSION_EXPIRED_MESSAGE;
    case 'otherTabLogout':
      return 'Kijelentkeztél egy másik lapon. Lépj be újra.';
    case 'otherUser':
      return 'Egy másik lapon más felhasználóval léptél be, ezért itt kijelentkeztettünk. Lépj be újra.';
    case 'otherUserReauth':
      return 'Más felhasználóval léptél be, ezért kijelentkeztettünk. Lépj be újra.';
    case 'permissionChanged':
      return 'Megváltozott a jogosultságod, ezért kijelentkeztettünk. Lépj be újra.';
    default:
      return null;
  }
}

/**
 * [2. javító kör — validátor F-B, 2026-10-03] „Újra-belépő ablak nyitva" jelző (`sessionStorage`,
 * fülenként). A mentetlen ágon a halott tokent töröljük, a `user` állapot marad — ha ilyenkor a
 * felhasználó újratölt (F5), az `AuthContext` init már nem lát tokent (`hadToken === false`), és
 * KORÁBBAN üzenet nélkül a nyilvános nyitóoldal jött. Ebből a jelzőből tudja az init, hogy a
 * belépő oldalra kell vinni (az üzenetet a `SESSION_NOTICE_KEY` adja). Az init EGYSZER fogyasztja
 * el; a sikeres újra-belépés és a munkamenet vége is törli.
 *
 * Miért nem `localStorage`: a `sessionStorage` fülenként külön — egy ÚJ fül (ahol sosem volt nyitva
 * ablak) így a szokásos, friss látogatást kapja (nyitóoldal). A `localStorage`-os
 * `auth_logout_reason` a következő sikeres belépésig ÉL, akár napokig: rá építve minden későbbi
 * friss látogatás (megosztott gépen más is) elavult „Lejárt…" üzenetet kapna. Az új fülön a
 * nyitóoldalról be lehet lépni, és az ugyanazzal a felhasználóval történt belépés a régi fül
 * ablakát is bezárja (`AuthContext` `storage`-kezelő) — ott nincs „élőhalott" állapot.
 */
export const REAUTH_PENDING_KEY = 'auth_reauth_pending';

export function setReauthPending(): void {
  try {
    sessionStorage.setItem(REAUTH_PENDING_KEY, '1');
  } catch {
    /* lásd fent */
  }
}

/** Kiolvassa ÉS törli a jelzőt (egyszeri fogyasztás az init-ben). */
export function consumeReauthPending(): boolean {
  try {
    const v = sessionStorage.getItem(REAUTH_PENDING_KEY) === '1';
    sessionStorage.removeItem(REAUTH_PENDING_KEY);
    return v;
  } catch {
    return false;
  }
}

export function clearReauthPending(): void {
  try {
    sessionStorage.removeItem(REAUTH_PENDING_KEY);
  } catch {
    /* lásd fent */
  }
}

/**
 * A kijelentkezés OKÁNAK jelzése a többi fülnek (`localStorage`, mert a `storage` esemény csak
 * ezt látja). A másik fül a `token` törlésekor kiolvassa: kézi kilépésnél „Kijelentkeztél egy
 * másik lapon", lejáratnál „Lejárt a bejelentkezésed". A következő sikeres belépés törli.
 */
export const LOGOUT_REASON_KEY = 'auth_logout_reason';

export function setLogoutReason(reason: 'manual' | 'expired'): void {
  try {
    localStorage.setItem(LOGOUT_REASON_KEY, reason);
  } catch {
    /* lásd fent */
  }
}

export function getLogoutReason(): 'manual' | 'expired' | null {
  try {
    const v = localStorage.getItem(LOGOUT_REASON_KEY);
    return v === 'manual' || v === 'expired' ? v : null;
  } catch {
    return null;
  }
}

export function clearLogoutReason(): void {
  try {
    localStorage.removeItem(LOGOUT_REASON_KEY);
  } catch {
    /* lásd fent */
  }
}
